import { create, type MessageInitShape } from "@bufbuild/protobuf";
import { type FieldMask } from "@bufbuild/protobuf/wkt";
import { type AssetType } from "#gen/asset/v1/asset_pb.js";
import { type AssetType as BundleAssetType } from "#gen/bundles/v1/bundle_pb.js";
import type { Code } from "#gen/codes/v3/codes_pb.js";
import { PaginationArgumentsSchema, type PaginationArguments } from "#gen/common/v1/pagination_pb.js";
import { BulkDownloadShortUrlsRequestSchema, EntrypointService, GetEntrypointRequestSchema, ListEntrypointsRequestSchema, type EntrypointBatch, type EntrypointBatchSchema, type GetEntrypointResponse } from "#gen/entrypoints/v1/entrypoints_pb.js";
import { createClient, type ClientOptions } from "#lib/api/api.js";
import { handleError } from "#lib/api/error.js";
import type { EntryAsset } from "#lib/assetManager.js";
import { PUBLIC_URLS } from "#lib/url.js";
import type { CallOptions, Client } from "@connectrpc/connect";

export function isEntryAssetCode(entryAsset: EntryAsset | undefined): entryAsset is Code {
	return entryAsset?.$typeName === "codes.v3.Code";
}

export class EntrypointsClient {
	private client: Client<typeof EntrypointService>;

	constructor(options?: () => ClientOptions) {
		// Use the same endpoint for codes and short urls
		this.client = createClient(PUBLIC_URLS.codes || "/", EntrypointService, options);
	}

	async updateBatch(batch: MessageInitShape<typeof EntrypointBatchSchema>, mask: FieldMask): Promise<EntrypointBatch> {
		try {
			const res = await this.client.updateEntrypointBatch({ batch, mask });
			return res.batch!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async countEntrypoints(orgId: string, workspaceId: string, batchId: string, assetType: AssetType, options?: CallOptions): Promise<number> {
		try {
			const res = await this.client.countEntrypoints(
				{
					orgId,
					workspaceId,
					batchId,
					assetTypes: [assetType],
				},
				options,
			);
			return res.count;
		} catch (e) {
			throw handleError(e);
		}
	}

	async bulkDownloadShortUrls(orgId: string, workspaceId: string, batchId: string, assetType: AssetType, emailRecipients: string[], pagination?: PaginationArguments, options?: CallOptions): Promise<string> {
		try {
			const res = await this.client.bulkDownloadShortUrls(
				create(BulkDownloadShortUrlsRequestSchema, {
					req: create(ListEntrypointsRequestSchema, {
						orgId,
						workspaceId,
						batchId,
						assetTypes: [assetType],
						pagination: pagination || create(PaginationArgumentsSchema, { first: 5000 }),
					}),
					emailRecipients: emailRecipients,
				}),
				options,
			);
			return res.url;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getEntrypointBatch(id: string): Promise<EntrypointBatch> {
		try {
			const res = await this.client.getEntrypointBatch({ id });
			return res.batch!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getEntrypoint({ id, omitDefaultMetadata }: { id: string; omitDefaultMetadata?: boolean }): Promise<GetEntrypointResponse> {
		try {
			const res = await this.client.getEntrypoint(create(GetEntrypointRequestSchema, { id, omitDefaultMetadata }));
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}
}

export enum CustomEntrypointType {
	// code mask urls are the short urls embedded in AssetType.CODE
	CODE_MASK_URL = "CODE_MASK_URL",

	OPEN_HOUSE_PLACEMENT = "OPEN_HOUSE_PLACEMENT",

	// code masks are the bottom and top lay of a Flowcode that can be used in print applications
	CODE_MASKS = "CODE_MASKS",
}
export type EntrypointType = BundleAssetType | CustomEntrypointType;
